import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { toIndoFormat } from "../../helper/helper";
import { FruitTraceability } from "../../models/traceability/FruitTraceability";
import { PalmIcon, RouteIcon, ScaleIcon, TrendIcon } from "./PurchaseReportIcons";
import { computePurchaseKpis } from "./purchaseReportMetrics";

type Props = {
    contracts: FruitTraceability[]
    startDate: dayjs.Dayjs
    endDate: dayjs.Dayjs
    /** Mill position, used for the mill-to-source distance. */
    millCoordinates?: number[]
}

type TileProps = {
    icon: React.ReactNode
    label: string
    value: string
    unit: string
    note: string
    /** The invoiced part of `value`, shown under it as a secondary figure. */
    invoiced?: InvoicedPart
}

type InvoicedPart = {
    value: string
    unit: string
    /** Invoiced share between 0 and 1, drawn as a bar; omitted when a share makes no sense (prices). */
    share?: number
}

const Tile: React.FC<TileProps> = ({ icon, label, value, unit, note, invoiced }) => {
    const { t } = useTranslation()

    return (
        <div className="ds-metric">
            <span className="ds-metric__label">
                <span className="ds-metric__icon">{icon}</span>
                {label}
            </span>
            <span className="ds-metric__value-row">
                <span className="ds-metric__value">{value}</span>
                <span className="ds-metric__unit">{unit}</span>
            </span>
            {invoiced && (
                <span className="ds-metric__part">
                    {invoiced.share !== undefined && (
                        <span className="ds-metric__part-bar">
                            <span
                                className="ds-metric__part-fill"
                                style={{ width: `${Math.min(Math.max(invoiced.share, 0), 1) * 100}%` }} />
                        </span>
                    )}
                    <span className="ds-metric__part-value">
                        {t('invoiced')} · {invoiced.value} {invoiced.unit}
                    </span>
                </span>
            )}
            <span className="ds-metric__note">{note}</span>
        </div>
    )
}

const NO_VALUE = "—"

export const PurchaseKpiBand: React.FC<Props> = ({ contracts, startDate, endDate, millCoordinates }) => {
    const { t } = useTranslation()
    const kpis = computePurchaseKpis(contracts, millCoordinates)

    const range = `${startDate?.format('DD MMM YYYY')} — ${endDate?.format('DD MMM YYYY')}`

    return (
        <div className="ds-panel purchase-report__kpis">
            <div className="ds-panel__head">
                <span className="ds-panel__head-label">{t('selected_period')} · {range}</span>
            </div>
            <div className="ds-panel__grid">
                <Tile
                    icon={<ScaleIcon color="var(--color-primary)" />}
                    label={t('total_volume')}
                    value={toIndoFormat(kpis.totalVolumeTons, 1)}
                    unit={t('unit_tonne')}
                    note={kpis.invoiced
                        ? t('on_n_contracts_n_invoiced', { count: kpis.contractCount, invoiced: kpis.invoiced.contractCount })
                        : t('on_n_contracts', { count: kpis.contractCount })}
                    invoiced={kpis.invoiced && {
                        value: toIndoFormat(kpis.invoiced.volumeTons, 1),
                        unit: t('unit_tonne'),
                        share: kpis.invoiced.volumeShare,
                    }}
                />
                <Tile
                    icon={<TrendIcon color="var(--color-primary)" />}
                    label={t('average_price')}
                    value={toIndoFormat(kpis.averagePrice, 0)}
                    unit={t('unit_rp_kg')}
                    note={t('weighted')}
                    invoiced={kpis.invoiced && {
                        value: toIndoFormat(kpis.invoiced.averagePrice, 0),
                        unit: t('unit_rp_kg'),
                    }}
                />
                <Tile
                    icon={<RouteIcon color="var(--color-primary)" />}
                    label={t('avg_delivery_range')}
                    value={kpis.avgDeliveryRangeKm === undefined ? NO_VALUE : toIndoFormat(kpis.avgDeliveryRangeKm, 1)}
                    unit={t('unit_km')}
                    note={t('mill_to_source')}
                />
                <Tile
                    icon={<PalmIcon color="var(--color-primary)" />}
                    label={t('total_fruit_source')}
                    value={toIndoFormat(kpis.totalFruitSources, 0)}
                    unit={t('sites')}
                    note={t('ramps_and_gardens')}
                />
            </div>
        </div>
    )
}
